// Phase 43.61 — Driver Operation Card (revisione logica)
// Card UNICA centro operativo. 5 stati distinti con SOLO la prossima azione
// reale immediata. Cromia unificata con customer:
//   ARANCIONE #F59E0B = prima del ritiro
//   BLU      #0052FF  = pacco ritirato/in viaggio/al destinatario
//   VERDE    #16A34A  = consegnato
import { Truck, Package, Navigation, MapPin, MapPinned, CheckCircle2 } from "lucide-react";
import { formatAddress } from "@/lib/formatAddress";

const STATE_BLOCKS = {
  accepted: {
    state: { label: "In viaggio verso il ritiro", icon: Truck, dot: "#F59E0B" },
    destLabel: "Indirizzo di ritiro",
    destField: "pickup_address",
    hint: "Raggiungi il punto di ritiro.",
    primaryCta: "Sono al ritiro",
    primaryAction: "advance",     // chiama advance() → picking_up
    showNavCta: true,
    navTarget: "pickup",
    accent: "#F59E0B",
    bg: "from-amber-50",
  },
  picking_up: {
    state: { label: "Al punto di ritiro", icon: MapPin, dot: "#F59E0B" },
    destLabel: "Indirizzo di ritiro",
    destField: "pickup_address",
    hint: "Conferma il ritiro del pacco.",
    primaryCta: "Conferma ritiro",
    primaryAction: "advance",     // → picked_up
    showNavCta: false,
    accent: "#F59E0B",
    bg: "from-amber-50",
  },
  picked_up: {
    state: { label: "Pacco ritirato · in viaggio", icon: Navigation, dot: "#0052FF" },
    destLabel: "Indirizzo di consegna",
    destField: "delivery_address",
    hint: "Raggiungi il destinatario.",
    primaryCta: "Apri navigatore",
    primaryAction: "navigate",    // apre maps esterno → delivery
    secondaryCta: "Sono al destinatario",
    secondaryAction: "advance",   // → delivering
    showNavCta: false,
    accent: "#0052FF",
    bg: "from-dapnow-blue-soft/60",
  },
  delivering: {
    state: { label: "Al punto di consegna", icon: Package, dot: "#0052FF" },
    destLabel: "Indirizzo di consegna",
    destField: "delivery_address",
    hint: "Conferma la consegna del pacco.",
    primaryCta: "Conferma consegna",
    primaryAction: "advance",     // → delivered
    showNavCta: false,
    accent: "#0052FF",
    bg: "from-dapnow-blue-soft/60",
  },
  delivered: {
    state: { label: "Consegnato", icon: CheckCircle2, dot: "#16A34A" },
    destLabel: null,
    destField: "delivery_address",
    hint: null,
    primaryCta: null,
    showNavCta: false,
    accent: "#16A34A",
    bg: "from-emerald-50",
  },
};

function buildMapsUrl(address) {
  if (!address) return null;
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}&travelmode=driving`;
}

export default function DriverOperationCard({ order, busy, onAdvance }) {
  const cfg = STATE_BLOCKS[order?.status];
  if (!cfg) return null;
  // delivered → la pagina mostra già il blocco "consegna completata"; evitiamo card vuota.
  if (order.status === "delivered") return null;
  const destAddress = order[cfg.destField] || "—";
  const destDisplay = destAddress !== "—" ? formatAddress(destAddress) : "—";
  const StateIcon = cfg.state.icon;
  const mapsUrl = buildMapsUrl(destAddress);

  // Phase 44.24 — Root cause schermata bianca post-pickup DEFINITIVO.
  // Differenza tra fase `accepted` (funziona) e `picked_up` (rompe):
  //   accepted → "Apri navigatore" era un <a target="_blank"> → iOS attiva
  //              il DEEP-LINK NATIVO all'app Google Maps. Quando l'utente
  //              esce da Maps con gesture back, torna a Safari → tab DapNow
  //              ancora viva ✓
  //   picked_up → "Apri navigatore" era un <button onClick={window.open()}> →
  //              window.open su iPhone Safari/PWA apre una NUOVA SCHEDA web
  //              Safari, NON l'app nativa. Quando l'utente esce, finisce
  //              nella nuova scheda Safari rimasta APERTA E VUOTA ("Cerca
  //              o inserisci sito web") → sembra schermata bianca ma è
  //              un'altra scheda. La tab DapNow originale è viva in
  //              background ma non visibile.
  // Fix: la fase picked_up usa lo stesso pattern <a> della fase accepted.
  const isNavigateAction = cfg.primaryAction === "navigate";
  const PrimaryEl = isNavigateAction && mapsUrl ? "a" : "button";
  const primaryProps = isNavigateAction && mapsUrl
    ? { href: mapsUrl, target: "_blank", rel: "noreferrer" }
    : { type: "button", onClick: () => onAdvance?.(), disabled: busy };

  return (
    <section
      className={`rounded-2xl border border-dapnow-line bg-gradient-to-br ${cfg.bg} via-white to-white shadow-md overflow-hidden`}
      data-testid="driver-operation-card"
    >
      {/* ── 1) STATO ATTUALE ─────────────────────────────────────────── */}
      <div className="px-4 pt-4 pb-3 border-b border-dapnow-line/60">
        <div className="flex items-center gap-2">
          <span className="relative inline-flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full rounded-full opacity-70 animate-ping" style={{ background: cfg.state.dot }} />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5" style={{ background: cfg.state.dot }} />
          </span>
          <p className="overline text-[10px] font-bold" style={{ color: cfg.accent }}>
            Stato attuale
          </p>
        </div>
        <div className="mt-1.5 flex items-center gap-2.5">
          <StateIcon className="h-5 w-5 shrink-0" style={{ color: cfg.accent }} />
          <p className="font-display text-base sm:text-lg font-bold text-dapnow-ink tracking-tight leading-tight" data-testid="driver-card-state">
            {cfg.state.label}
          </p>
        </div>
      </div>

      {/* ── 2) DESTINAZIONE ──────────────────────────────────────────── */}
      {cfg.destLabel && (
        <div className="px-4 py-3 border-b border-dapnow-line/60 bg-white/60">
          <div className="flex items-center gap-2">
            <MapPinned className="h-3.5 w-3.5 text-dapnow-muted" />
            <p className="overline text-[10px] text-dapnow-muted">{cfg.destLabel}</p>
          </div>
          <p className="mt-1 text-sm font-semibold text-dapnow-ink leading-snug break-words" data-testid="driver-card-destination">
            {destDisplay}
          </p>
        </div>
      )}

      {/* ── 3) AZIONE RICHIESTA ──────────────────────────────────────── */}
      {cfg.primaryCta && (
        <div className="px-4 py-4 bg-white space-y-2.5">
          <p className="overline text-[10px] font-bold text-dapnow-ink">Azione richiesta</p>
          {cfg.hint && (
            <p className="text-sm text-dapnow-muted leading-snug">{cfg.hint}</p>
          )}
          <PrimaryEl
            {...primaryProps}
            data-testid="driver-card-action-btn"
            className="w-full inline-flex items-center justify-center gap-2 rounded-xl text-white px-4 h-12 text-sm font-bold tracking-tight shadow-md transition disabled:opacity-60"
            style={{ background: cfg.accent }}
          >
            {cfg.primaryAction === "navigate" && <Navigation className="h-4 w-4" />}
            {busy && !isNavigateAction ? "Aggiorno…" : cfg.primaryCta}
          </PrimaryEl>

          {/* CTA secondaria: variabile per stato */}
          {cfg.secondaryCta && (
            <button
              type="button"
              onClick={() => cfg.secondaryAction === "advance" && onAdvance?.()}
              disabled={busy}
              data-testid="driver-card-secondary-btn"
              className="w-full inline-flex items-center justify-center gap-2 rounded-xl border-2 border-dapnow-line bg-white px-4 h-11 text-sm font-semibold text-dapnow-ink hover:border-dapnow-blue transition disabled:opacity-60"
            >
              {cfg.secondaryCta}
            </button>
          )}
          {/* "Apri navigatore" outline secondario per fase accepted */}
          {cfg.showNavCta && mapsUrl && (
            <a
              href={mapsUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="driver-card-nav-btn"
              className="w-full inline-flex items-center justify-center gap-2 rounded-xl border-2 border-dapnow-line bg-white px-4 h-11 text-sm font-semibold text-dapnow-ink hover:border-dapnow-blue transition"
            >
              <Navigation className="h-4 w-4" />
              Apri navigatore
            </a>
          )}
        </div>
      )}
    </section>
  );
}
